import { saveDecidedPrice, saveDecidedPricesBatch } from '../api/api';
import {
  MAX_DECIDED_BATCH, STORE_MISMATCH, type DecidedBatchItem, type DecidedPriceResult,
} from '../types/decidedPrice';
import {
  MISSING_ROW, chunk, currentRequestStore, currentSessionKey, isInFlightMessage, loadPending, requestIdForSave, saveErrorOutcome, saveSettled, settleSave,
  type SaveErrorOutcome,
} from './decidedPrice';

/**
 * 여러 장 판매가 저장(스토어 상품 관리, 감사 card-controltower#649 · #819) — 백엔드 PR #97 의 `POST /decided-price/batch` 로 20장씩 차례로
 * 보낸다(한 번에 하나 — 예전 단건 동시 호출은 매장 가격 쓰기 락이 겹쳐 409 가 났다).
 *
 * - 409 `{message, retryAfterMs}` 면 그 시간 뒤 **같은 requestId** 로 다시(최대 3회) — 끝난 항목은 서버가 첫 결과를 돌려준다.
 * - 옛 서버(batch 404)면 남은 카드를 단건 PUT 으로 한 건씩(예전 경로).
 * - 보내기 전마다 세션(계정 · 매장 · 로그인 시각)을 확인해 바뀌었으면 남은 카드는 보내지 않고 그 요청 번호를 지운다(카드 id 는 매장마다
 *   겹친다 — 서버도 판매자 상품코드로 막는다, 감사 #855 · #856).
 */

/** 줄 하나의 결과. */
export type DecidedSaveOutcome =
  | { kind: 'saved'; result: DecidedPriceResult }
  /** failed = 저장 안 됨(새로 보내도 된다) · unknown = 저장됐는지 모름 · busy = 서버가 같은 요청 처리 중 · mismatch = 판매자 상품코드 불일치. */
  | { kind: 'failed' | 'unknown' | 'busy' | 'mismatch'; message: string }
  /** 매장이 바뀌어 보내지 않았다. */
  | { kind: 'notSent'; message: string };

export interface DecidedSaveEntry {
  cardId: number;
  price:  number;
  /** 목록의 판매자 상품코드(있으면 실어 보낸다). */
  sellerProductCode?: string | null;
}

export interface DecidedSaveProgress {
  /** 끝난(결과를 받은) 카드 수. */
  done:   number;
  total:  number;
  /** 그중 저장 못 한 수(실패 · 불명 · 처리 중 · 불일치 · 안 보냄). */
  failed: number;
}

export interface DecidedSaveDeps {
  onProgress?: (p: DecidedSaveProgress) => void;
  /** 테스트용 — 기다리기. */
  sleep?: (ms: number) => Promise<void>;
}

export const BATCH_RETRY_MAX = 3;
/** 서버 힌트가 지나치게 길어도 이 안에서 다시 보낸다. */
export const BATCH_RETRY_WAIT_CAP_MS = 30_000;
export const STORE_MISMATCH_MESSAGE = '목록이 바뀌었습니다(판매자 상품코드 불일치) — 목록을 새로 받으세요';
const STORE_CHANGED_MESSAGE = '매장이 바뀌어 보내지 않았습니다 — 목록을 새로 받으세요';
export const SESSION_CHANGED_MESSAGE = '로그인이 바뀌어 보내지 않았습니다 — 다시 로그인한 계정으로 목록을 새로 받은 뒤 저장하세요';

/**
 * 묶음을 시작한 세션 · 매장 그대로인가(감사 card-controltower#855) — 아니면 보내지 않을 사유. 409 를 기다리는 사이 로그아웃 · 같은 매장 다른
 * 계정 · 다시 로그인하면 쿠키가 바뀌어 앞 계정의 저장을 새 계정으로 보내게 된다(매장 문자열만 보면 같은 매장 다른 계정을 못 가린다).
 */
interface SendScope {
  store: string | null;
  session: string;
  /**
   * 이번 묶음에서 새로 만든 요청 번호의 카드(감사 #873) — 멈출 때 이것만 지운다. 앞 저장의 '결과 불명' 번호를 다시 쓴 카드(같은 가격)는 그
   * 번호가 서버에서 처리됐을 수 있어 지우면 다음 저장이 새 번호로 두 번 반영될 수 있다 — 남긴다.
   */
  fresh: Set<number>;
}
function movedReason(scope: SendScope): string | null {
  if (currentSessionKey() === scope.session) return null;
  return currentRequestStore() !== scope.store && currentSessionKeyWithoutStore() === sessionWithoutStore(scope.session)
    ? STORE_CHANGED_MESSAGE : SESSION_CHANGED_MESSAGE;
}
/** 세션 열쇠에서 매장 칸(store · superStore)을 뺀 것 — 매장만 바뀌었는지 가리는 데. */
const sessionWithoutStore = (key: string): string => { const p = key.split('|'); return [p[0], p[3], p[4]].join('|'); };
const currentSessionKeyWithoutStore = (): string => sessionWithoutStore(currentSessionKey());

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** 409 의 다시 보낼 때까지(ms) — 본문 retryAfterMs, 없으면 null(다시 보내지 않는다). */
export function retryAfterOf(e: unknown): number | null {
  const res = (e as { response?: { status?: number; data?: { retryAfterMs?: unknown } } } | null)?.response;
  if (res?.status !== 409) return null;
  const ms = res.data?.retryAfterMs;
  return typeof ms === 'number' && Number.isFinite(ms) && ms >= 0 ? Math.min(ms, BATCH_RETRY_WAIT_CAP_MS) : null;
}

const statusOf = (e: unknown): number | undefined => (e as { response?: { status?: number } } | null)?.response?.status;

const fromError = (o: SaveErrorOutcome): DecidedSaveOutcome => ({ kind: o.kind, message: o.message });

/** 단건 저장 결과 · 오류 → 줄 결과 + 요청 번호 정리. */
function settleSingle(store: string | null, item: DecidedBatchItem, r: DecidedPriceResult): DecidedSaveOutcome {
  if (saveSettled(r.naver.status)) settleSave(store, item.cardId, item.requestId);
  return { kind: 'saved', result: r };
}

async function sendSingle(store: string | null, item: DecidedBatchItem): Promise<DecidedSaveOutcome> {
  try {
    // 옛 서버 경로 — 새 칸(sellerProductCode)은 싣지 않는다.
    return settleSingle(store, item, await saveDecidedPrice(item.cardId, { price: item.price, requestId: item.requestId }));
  } catch (e) {
    const o = saveErrorOutcome(e);
    if (saveSettled(null, o.kind)) settleSave(store, item.cardId, item.requestId);
    return fromError(o);
  }
}

/** batch 한 번(409 다시 보내기 포함). 404 면 'fallback'. */
async function sendBatch(
  scope: SendScope,
  items: DecidedBatchItem[],
  sleep: (ms: number) => Promise<void>,
): Promise<Map<number, DecidedSaveOutcome> | 'fallback'> {
  const { store } = scope;
  const out = new Map<number, DecidedSaveOutcome>();
  // 마지막 409 가 '같은 요청 처리 중' 이었나 — 그러면 서버가 그 번호를 처리하고 있을 수 있어 멈춰도 번호를 지우지 않는다(감사 #856).
  let inFlight = false;
  for (let attempt = 0; ; attempt += 1) {
    try {
      const res = await saveDecidedPricesBatch(items);
      for (const row of res.results) {
        const item = items.find((i) => i.cardId === row.cardId)!;
        if (row.ok) {
          // 저장은 됐다 — 결과 본문이 없으면 네이버 결과는 모른다(같은 번호로만 다시).
          out.set(row.cardId, settleSingle(store, item, row.result ?? {
            cardId: item.cardId, price: item.price, naver: { status: 'UNKNOWN', message: '네이버 결과를 받지 못했습니다' }, hub: null,
          }));
        } else if (row.error === STORE_MISMATCH) {
          // 서버가 쓰지 않았다 — 번호를 지운다(같은 목록으로는 다시 보내도 같은 결과).
          settleSave(store, item.cardId, item.requestId);
          out.set(row.cardId, { kind: 'mismatch', message: STORE_MISMATCH_MESSAGE });
        } else if (row.error === MISSING_ROW) {
          out.set(row.cardId, { kind: 'unknown', message: `${MISSING_ROW} — 결과 불명` });
        } else if (isInFlightMessage(row.error)) {
          out.set(row.cardId, { kind: 'busy', message: `이미 처리 중 — ${row.error}` });
        } else {
          settleSave(store, item.cardId, item.requestId);
          out.set(row.cardId, { kind: 'failed', message: row.error ?? '저장하지 못했습니다' });
        }
      }
      return out;
    } catch (e) {
      if (statusOf(e) === 404 && attempt === 0) return 'fallback';
      const wait = retryAfterOf(e);
      if (wait != null && attempt < BATCH_RETRY_MAX) {
        inFlight = isInFlightMessage((e as { response?: { data?: { message?: unknown } } })?.response?.data?.message as string | undefined);
        await sleep(wait);
        const moved = movedReason(scope);
        if (moved) {
          // 409(락이 바쁨)는 서버가 처리하지 않은 것 — 번호를 지워 판매가 결정 화면이 '결과 불명' 으로 잡지 않게(감사 #856).
          // '같은 요청 처리 중' 이었으면 그 번호를 서버가 처리하고 있을 수 있어 남긴다.
          markNotSent(out, items, moved, inFlight ? null : store, scope.fresh);
          return out;
        }
        continue;
      }
      // 다시 보낸 요청(attempt>0)의 409 · 401 · 403 은 첫 요청 결과를 모르는 것 — 번호를 지우지 않는다(saveErrorOutcome retry).
      const o = saveErrorOutcome(e, { retry: attempt > 0 });
      items.forEach((i) => {
        if (saveSettled(null, o.kind)) settleSave(store, i.cardId, i.requestId);
        out.set(i.cardId, fromError(o));
      });
      return out;
    }
  }
}

/**
 * 보내지 않은 줄 — clearStore 를 주면 그 매장 보관소의 요청 번호도 지운다(보낸 적 없는 번호가 '결과 불명' 으로 남지 않게, 감사 #856). 단 이번
 * 묶음에서 새로 만든 번호(fresh)만 — 앞 저장의 결과 불명 번호를 다시 쓴 카드는 남긴다(감사 #873).
 */
function markNotSent(
  out: Map<number, DecidedSaveOutcome>, items: DecidedBatchItem[], message: string, clearStore: string | null | undefined, fresh: Set<number>,
): void {
  for (const i of items) {
    if (clearStore !== undefined && clearStore !== null && fresh.has(i.cardId)) settleSave(clearStore, i.cardId, i.requestId);
    out.set(i.cardId, { kind: 'notSent', message });
  }
}

/** 요청 번호 — 보관소의 같은 가격 번호(앞 저장이 결과 불명)를 다시 쓰면 그대로, 아니면 새로 만들고 fresh 에 적는다. */
function freshOrReused(
  fresh: Set<number>, prev: { price: number } | undefined, store: string | null, cardId: number, price: number,
): string {
  if (!prev || prev.price !== price) fresh.add(cardId);
  return requestIdForSave(store, cardId, price);
}

/**
 * 판매가 여러 장 저장 — 카드 id → 결과. 0원 이하는 부른 쪽이 거른다. 결과 불명 뒤 같은 가격이면 같은 requestId(#651 보관소).
 */
export async function saveDecidedPricesInBatches(
  entries: DecidedSaveEntry[],
  deps: DecidedSaveDeps = {},
): Promise<Map<number, DecidedSaveOutcome>> {
  const sleep = deps.sleep ?? defaultSleep;
  const store = currentRequestStore();
  const scope: SendScope = { store, session: currentSessionKey(), fresh: new Set<number>() };
  const pending = loadPending(store);
  const items: DecidedBatchItem[] = entries.map((e) => ({
    cardId: e.cardId,
    price: e.price,
    requestId: freshOrReused(scope.fresh, pending.get(e.cardId), store, e.cardId, e.price),
    ...(e.sellerProductCode ? { sellerProductCode: e.sellerProductCode } : {}),
  }));
  const out = new Map<number, DecidedSaveOutcome>();
  const report = () => {
    let failed = 0;
    out.forEach((o) => { if (o.kind !== 'saved') failed += 1; });
    deps.onProgress?.({ done: out.size, total: items.length, failed });
  };
  report();
  let single = false;
  const chunks = chunk(items, MAX_DECIDED_BATCH);
  for (let ci = 0; ci < chunks.length; ci += 1) {
    const part = chunks[ci];
    const moved = movedReason(scope);
    if (moved) {
      markNotSent(out, chunks.slice(ci).flat(), moved, store ?? undefined, scope.fresh);
      report();
      break;
    }
    if (!single) {
      const r = await sendBatch(scope, part, sleep);
      if (r !== 'fallback') {
        r.forEach((o, id) => out.set(id, o));
        report();
        continue;
      }
      single = true; // 옛 서버 — 이 묶음부터 단건으로
    }
    for (const item of part) {
      const movedNow = movedReason(scope);
      if (movedNow) {
        markNotSent(out, [item], movedNow, store ?? undefined, scope.fresh);
      } else {
        out.set(item.cardId, await sendSingle(store, item));
      }
      report();
    }
  }
  return out;
}
